import { nanoid } from "nanoid";
import { dataSyncEmitter } from "@/modules/events/emitter";
import CrashReporter from "@/modules/app/CrashReporter";
import { getReplayDuration } from "@/modules/replay/constraints";
import { useStation } from "camera/store/station";
import { usePendingEvents } from "camera/store/pendingEvents";
import DB, { DATE_INDEX } from "./DB";
import { EVENT_DELETE_INTERVAL, MAXIMUM_EVENT_DURATION } from "../events/constants";
export default class LocalEvents {
  private static db: DB | null = null;
  private static interval: NodeJS.Timeout | null = null;
  private static resolvers: { [id: string]: (db: DB) => void } = {};
  private static finishedClosingEvents: Promise<void>;

  static closeAndAddUnfinishedEventsToQueue = async () => {
    const lastSegmentEnd = usePendingEvents.getState().lastUploadedSegmentEndTime;
    this.finishedClosingEvents = Promise.all(
      usePendingEvents.getState().unfinishedEvents.map(async (e) => {
        e.finished = true;
        if (lastSegmentEnd && lastSegmentEnd < e.end && lastSegmentEnd >= e.start) {
          log.events("Adjusting unfinished event end time", { uniqueId: e.uniqueId, from: e.end, to: lastSegmentEnd });
          e.end = lastSegmentEnd;
        }
        log.events("Closing unfinished event", e);
        await LocalEvents.save(e);
        return e;
      })
    )
      .then((events) => {
        const closedIds = events.map((e) => e.uniqueId);
        usePendingEvents.setState(({ events: currentEvents, unfinishedEvents }) => ({
          unfinishedEvents: unfinishedEvents.filter((e) => !closedIds.includes(e.uniqueId)),
          events: [...events, ...currentEvents]
        }));
      })
      .catch((err) => {
        log.err("Encountered error in 'closeAndAddUnfinishedEventsToQueue'");
        log.err(err);
      });
  };

  private static getDBAsync = async (name: string): Promise<DB> => {
    if (this.db) return this.db;
    return new Promise<DB>((resolve) => {
      const id = nanoid();
      this.resolvers[id] = (db) => {
        resolve(db);
        delete this.resolvers[id];
        log.info(`Resolved DB getter for '${name}'`, this.resolvers);
      };
    });
  };

  private static processResolvers = (db: DB) => {
    Object.values(this.resolvers).forEach((cb) => cb(db));
  };

  static init = async () => {
    const db = new DB();
    await db.init();
    this.db = db;
    this.processResolvers(db);
  };

  static save = async (event: CameraEvent, { shouldAnnounce } = { shouldAnnounce: false }) => {
    const db = await this.getDBAsync("save");
    try {
      await db.getWrite<DbCameraEvent>("local-events")({
        ...event,
        finished: event.finished ? 1 : 0
      });
    } catch (err) {
      log.err(`Couldn't save local event ${event.uniqueId}`, err);
      CrashReporter.sendException("Couldn't save local event", { key: "err", extra: err });
    }

    if (!shouldAnnounce) return;
    if (event.intervalEvent && !event.finished) {
      dataSyncEmitter.emit("local-event-create", event);
    } else if (event.intervalEvent) dataSyncEmitter.emit("local-event-update", event);
  };

  static startRemovingOldEvents = async () => {
    try {
      if (this.interval) return;
      log.events("'startRemovingOldEvents' called");
      await this.deleteOld();
      this.interval = setInterval(this.deleteOld, EVENT_DELETE_INTERVAL);
    } catch (err) {
      CrashReporter.sendException("startRemovingOldEvents failed", { key: "err", extra: err });
      log.err("'startRemovingOldEvents' failed, trying again in 5s...");
      setTimeout(this.startRemovingOldEvents, 5000);
    }
  };

  static getAll = async (): Promise<CameraEvent[]> => {
    const db = await this.getDBAsync("getAll");
    if (!db.isOpen()) return [];
    const events = (await db.readAll<DbCameraEvent | null>("local-events")).filter((e): e is DbCameraEvent =>
      Boolean(e)
    );
    log.events("'getAll' result", events);
    return events.sort((a, b) => a.start - b.start).map((e) => ({ ...e, finished: Boolean(e.finished) }));
  };

  static getFirstFound = async (direction?: "oldest" | "newest") => {
    await this.finishedClosingEvents;
    const db = await this.getDBAsync("getFirstFound");
    if (!db.isOpen()) return null;
    const objectStore = db.createTransaction("readonly", "local-events");
    const index = objectStore.index(DATE_INDEX);
    const request = index.openCursor(null, direction === "oldest" ? "next" : "prev");

    return new Promise<CameraEvent | null>((resolve) => {
      request.onsuccess = (e) => {
        const cursor = (e.target as IDBRequest<IDBCursorWithValue>).result;
        if (!cursor) return resolve(null);
        const event = cursor.value as DbCameraEvent;
        if (!event) {
          log.err("Invalid cursor value for local event in getFirstFound, skipping key", cursor.primaryKey);
          cursor.continue();
          return;
        }
        if (event.type === "MOTION" || event.type === "NOISE") {
          return resolve({ ...event, finished: Boolean(event.finished) });
        }
        cursor.continue();
      };
    });
  };

  private static deleteOld = async () => {
    const replayDuration = getReplayDuration(useStation.getState().settings.replayDuration);
    log.events("'deleteOld' called with time", replayDuration);
    const db = await this.getDBAsync("deleteOld");
    if (!db.isOpen()) return;
    const upperBound = Date.now() - replayDuration;
    const range = IDBKeyRange.upperBound(upperBound);

    const index = db.createTransaction("readwrite", "local-events").index(DATE_INDEX);
    const request = index.openCursor(range);

    return new Promise<void>((resolve) => {
      request.onsuccess = (e) => {
        const cursor = (e.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          const event = cursor.value as DbCameraEvent;
          if (!event) {
            log.err("Invalid cursor value for local event in deleteOld, deleting key", cursor.primaryKey);
            cursor.delete();
            cursor.continue();
            return;
          }
          if (event.finished === 1) {
            log.events("About to delete local event", event.uniqueId);
            cursor.delete();
          }
          cursor.continue();
        } else resolve();
      };
      request.onerror = (e) => {
        log.err("'deleteOld' failed", e);
        resolve();
      };
    });
  };

  static deleteBrokenEvents = async () => {
    const db = await this.getDBAsync("deleteBrokenEvents");
    if (!db.isOpen()) return;
    const upperBound = Date.now() - MAXIMUM_EVENT_DURATION * 2;
    const range = IDBKeyRange.upperBound(upperBound);
    const index = db.createTransaction("readwrite", "local-events").index(DATE_INDEX);
    const request = index.openCursor(range);

    return new Promise<void>((resolve) => {
      request.onsuccess = (e) => {
        const cursor = (e.target as IDBRequest<IDBCursorWithValue>).result;
        if (cursor) {
          const event = cursor.value as DbCameraEvent;
          if (!event) {
            log.err("Invalid cursor value for local event in deleteBrokenEvents, deleting key", cursor.primaryKey);
            cursor.delete();
            cursor.continue();
            return;
          }
          if (event.finished === 0) {
            log.warn("Found broken local event", event.uniqueId, event.start, event.end);
            CrashReporter.sendMessage("Found broken local event");
            cursor.delete();
          }
          cursor.continue();
        } else resolve();
      };
      request.onerror = (e) => {
        log.err("'deleteOld' failed", e);
        resolve();
      };
    });
  };

  static clearAll = async () => {
    const db = await this.getDBAsync("clearAll");
    await db.clearAll("local-events");
  };
}
